import { useState } from "react";
import { formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Download, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { buildTimelineEntries } from "@/lib/employeeConstants";
import { generateTimelinePdf } from "@/lib/timelinePdf";
import TimeDeltaBadge from "@/components/TimeDeltaBadge";

const TIMELINE_PILLS = [
  { key: "absence", label: "Absences", color: "bg-slate-100" },
  { key: "bonus", label: "Primes", color: "bg-green-100 text-green-800" },
  { key: "pv", label: "PV", color: "bg-red-100 text-red-800" },
  { key: "training", label: "Formations", color: "bg-blue-100 text-blue-800" },
  { key: "life-event", label: "Marquants", color: "bg-amber-100 text-amber-800" },
  { key: "sav", label: "SAV", color: "bg-purple-100 text-purple-800" },
  { key: "chantier", label: "Chantiers", color: "bg-indigo-100 text-indigo-800" },
];

export const ChronologieTab = ({
  employee, absences, bonuses, pvs, trainings, lifeEvents, savs, orders,
  filter, setFilter,
}) => {
  const [expanded, setExpanded] = useState({});

  const entries = buildTimelineEntries({
    absences, bonuses, pvs, trainings, lifeEvents, savs, orders,
    filter, formatCurrency,
  });

  const toggleExpand = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  const handleExportPdf = () => {
    if (entries.length === 0) {
      toast.info("Aucune entrée à exporter sur la période choisie");
      return;
    }
    const periodLabel =
      filter.from || filter.to
        ? `${filter.from ? new Date(filter.from).toLocaleDateString("fr-FR") : "—"} → ${filter.to ? new Date(filter.to).toLocaleDateString("fr-FR") : "—"}`
        : "Toutes périodes confondues";
    generateTimelinePdf({
      employee: { name: `${employee.first_name} ${employee.last_name}` },
      periodLabel,
      entries,
    });
  };

  // Group by year
  const byYear = {};
  entries.forEach((ev) => {
    const y = ev.date ? new Date(ev.date).getFullYear() : "—";
    (byYear[y] = byYear[y] || []).push(ev);
  });

  return (
    <div className="space-y-4">
      <Card data-testid="timeline-card">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Carnet de bord RH
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Toutes les entrées chronologiques du salarié, rassemblées sur un même axe.
              </p>
            </div>
          </div>

          <div className="pt-3 border-t mt-3 flex flex-wrap items-end gap-3" data-testid="timeline-toolbar">
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-xs">Du</Label>
                <Input
                  type="date" value={filter.from}
                  onChange={(e) => setFilter({ ...filter, from: e.target.value })}
                  className="h-8 w-[155px]" data-testid="timeline-from"
                />
              </div>
              <div>
                <Label className="text-xs">Au</Label>
                <Input
                  type="date" value={filter.to}
                  onChange={(e) => setFilter({ ...filter, to: e.target.value })}
                  className="h-8 w-[155px]" data-testid="timeline-to"
                />
              </div>
              {(filter.from || filter.to) && (
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setFilter({ ...filter, from: "", to: "" })}
                  data-testid="timeline-reset"
                >
                  Effacer
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {TIMELINE_PILLS.map((t) => {
                const on = !!filter.types[t.key];
                return (
                  <button
                    type="button" key={t.key}
                    onClick={() =>
                      setFilter({ ...filter, types: { ...filter.types, [t.key]: !on } })
                    }
                    className={`text-xs px-2 py-1 rounded-full border transition-opacity ${t.color} ${on ? "" : "opacity-40"}`}
                    data-testid={`timeline-filter-${t.key}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <div className="ml-auto">
              <Button size="sm" variant="outline" onClick={handleExportPdf} data-testid="timeline-export-pdf">
                <Download className="h-4 w-4 mr-1" /> Export PDF
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-6 bg-muted/30 rounded" data-testid="timeline-empty">
              Aucun événement sur la période sélectionnée.
            </p>
          ) : (
            <div className="space-y-6" data-testid="timeline-entries">
              {Object.keys(byYear)
                .sort((a, b) => String(b).localeCompare(String(a)))
                .map((y) => (
                  <div key={y}>
                    <h3 className="text-sm font-heading font-bold text-muted-foreground mb-3 sticky top-0 bg-card py-1">
                      {y}
                    </h3>
                    <div className="relative border-l-2 border-border pl-4 space-y-3">
                      {byYear[y].map((ev) => {
                        const expandable = ev.category === "chantier" && ev.meta?.days?.length > 0;
                        const isOpen = !!expanded[ev.key];
                        return (
                          <div key={ev.key} className="relative" data-testid={`timeline-entry-${ev.category}`}>
                            <span className="absolute -left-[22px] top-1 h-3 w-3 rounded-full bg-primary ring-4 ring-background" />
                            <div className="flex items-start gap-3">
                              <span className="text-xs font-mono tabular-nums text-muted-foreground min-w-[90px]">
                                {ev.date
                                  ? new Date(ev.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
                                  : "—"}
                                {ev.time ? ` · ${ev.time}` : ""}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant="secondary" className={`${ev.color} text-xs`}>
                                    {ev.label}
                                  </Badge>
                                  <span className="text-sm font-medium">{ev.title}</span>
                                  {ev.category === "chantier" && ev.meta?.time_delta && (
                                    <TimeDeltaBadge delta={ev.meta.time_delta} compact />
                                  )}
                                  {expandable && (
                                    <button
                                      type="button"
                                      onClick={() => toggleExpand(ev.key)}
                                      className="ml-auto text-xs text-primary hover:underline flex items-center gap-0.5"
                                      data-testid={`timeline-chantier-toggle-${ev.meta.order_id}`}
                                    >
                                      {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                      {isOpen ? "Masquer" : "Détail"} ({ev.meta.days.length} j)
                                    </button>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-wrap">
                                  {ev.description}
                                </p>
                                {expandable && isOpen && (
                                  <div className="mt-2 border rounded-md bg-muted/30 p-2 space-y-1" data-testid={`timeline-chantier-details-${ev.meta.order_id}`}>
                                    {ev.meta.days.map((d) => (
                                      <div key={d.date} className="flex items-center justify-between text-xs font-mono">
                                        <span>{new Date(d.date).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}</span>
                                        <span className="text-primary font-semibold">{d.hours} h</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ChronologieTab;
